//
// 3D Rotation input controls. Adapted from MentalCanvas C++ code.
// (c) 2016-2020 Mental Canvas Inc.

import {
    Camera,
    Quaternion,
    Vector2,
    Vector3
} from 'three';
import {
    _Math,
    RBT,
    SphericalWithRoll
} from './utils';
import {
    ControlLimits
} from './MCControls';
import {
    MCScene
} from './mcsrc';

export class ArcBall
{
    camera: Camera;
    viewportDims: Vector2;
    limits: ControlLimits;
    constraintFrame: RBT;
    private m_center: Vector3 = new Vector3;
    bIsCollabSession: boolean = false;
    bEgocentric: boolean = false;
    private m_RadSqr : number = 0;
    private m_prevViewportDims : Vector2;
    private m_Scene : MCScene;

    // Holds a reference to InViewportDims, just like C++.
    constructor(InScene: MCScene, InCamera: Camera, InViewportDims: Vector2, InLimits: ControlLimits, InConstraintFrame: RBT)
    {
        this.m_Scene = InScene;
        this.camera = InCamera;
        this.viewportDims = InViewportDims;
        this.m_prevViewportDims = new Vector2(-1,-1);
        this.limits = InLimits;
        this.constraintFrame = InConstraintFrame;
    }
    dispose() {
        this.m_Scene = null;
    }
    SetCenter(InCenter: Vector3)
    {
        this.m_center.copy(InCenter);
        // No constraints present: arcball takes up 100% of screen, so a top-to-bottom swipe
        // corresponds to 180 degrees of motion (i.e., rotate top of ball to bottom of screen).
        var constraintScale : Vector2 = new Vector2(1, 1);
        // With constraints: arcball is bigger than screen, such that top-to-bottom swipe
        // corresponds to less than 180 degrees feasible range of motion. For example, if
        // only +/- 30 degrees of motion are feasible, the ball is sized so that the 90% mark of
        // the screen is at the +30 degrees point on the ball, and the 10% mark is at -30 degrees.
        //
        // (80% below corresponds to 80% of the way from centre to top, or the 90% point on the screen in
        //  the discussion above.)

        // Collaboration: use normal default range, even though we allow an expanded actual range.
        const azimuthRangeRad : number = this.bIsCollabSession ? 120 * _Math.DEG2RAD : this.limits.AzimuthRangeRad();
        if (this.limits.Azimuth || this.bIsCollabSession)
            constraintScale.x = Math.min(Math.abs(Math.sin(azimuthRangeRad / 2)) / 0.8, 1.0);
        // Collaboration: use normal default range, even though we allow an expanded actual range.
        const elevationRangeRad : number = this.bIsCollabSession ? 40 * _Math.DEG2RAD
            : this.limits.ElevationMaxRad() - this.limits.ElevationMinRad();
        if (this.limits.Elevation)
            constraintScale.y = Math.min(Math.abs(Math.sin(elevationRangeRad / 2)) / 0.8, 1.0);

        // This depends on "no roll" - but there is never any roll in the frame where the action is
        // applied, the constraint frame.
        this.m_RadSqr = _Math.sqr(Math.min(this.viewportDims.x / constraintScale.x,
                                    this.viewportDims.y / constraintScale.y) / 2.0 );
    }
    GetArcballDir(p: Vector2): Vector3 {
        if (this.m_prevViewportDims.distanceTo(this.viewportDims) > 0.01) {
            // Recalcluate m_RadSqr
            this.SetCenter(this.m_center);
            this.m_prevViewportDims.copy(this.viewportDims);
        }
        return new Vector3(p.x, p.y, Math.max(Math.sqrt(this.m_RadSqr) - p.length(), 0.0)).normalize();
    }
    MoveArcball(p: Vector2, origin: Vector2, stiffness: number): Quaternion {
        // Difference from authoring: create a ball centered at touch-down point, instead of screen center.
        // This gives more predictable results for the user: only ever regular axial rotations.
        // (Identical approach to egocentric, but opposite direction.)
        const BallScreenCenter : Vector2 = origin; //this.viewportDims.clone().multiplyScalar(0.5);
        var v0 : Vector3 = this.GetArcballDir(p.clone().sub(BallScreenCenter));
        const v1 : Vector3 = this.GetArcballDir(origin.clone().sub(BallScreenCenter));
        const angle_rad : number = Math.acos(Math.min(v0.dot(v1), 1)) / stiffness;
        var result : Quaternion = new Quaternion;
        v0.cross(v1).normalize();
        return result.setFromAxisAngle(v0, angle_rad);
    }
    MoveEgocentric(p: Vector2, origin: Vector2): Quaternion {
        const v0 : Vector3 = this.GetArcballDir(p.clone().sub(origin));
        const v1 : Vector3 = this.GetArcballDir(new Vector2(0,0));
        const angle_rad : number = Math.acos(Math.min(v0.dot(v1), 1));
        var result : Quaternion = new Quaternion;
        v0.cross(v1).normalize();
        return result.setFromAxisAngle(v0, -angle_rad);
    }
    LimitSpherical(sph: SphericalWithRoll) : SphericalWithRoll {
        var result : SphericalWithRoll = sph.clone();
        // Rubber band effect part #2. After releasing the rubber band (letting go of finger), it returns to the
        // natural position (the maximum viewing angle).
        if (this.limits.Azimuth)
            result.theta = _Math.clamp(result.theta, this.limits.AzimuthMinRad(), this.limits.AzimuthMaxRad());
        if (this.limits.Elevation)
            result.setElevation(_Math.clamp(result.getElevation(), this.limits.ElevationMinRad(), this.limits.ElevationMaxRad()));
        return result;
    }
    ApplyAction(action: RBT) {
        const result : RBT = action.doToWRT(
            new RBT(this.camera.quaternion, this.camera.position),
            new RBT(this.camera.quaternion, this.bEgocentric? this.camera.position : this.m_center));
        this.camera.position.copy(result.translation);
        this.camera.quaternion.copy(result.rotation);
    }
    // preRoll only required for rubberband=none. Corresponds to roll *before user input*.
    // dt only required for rubberband != none
    ApplyConstraints(rubberband: 'none' | 'rollrubberband' | 'rubberband', dt: number, preRoll?: number) {
        // 1st term: transform from worldspace to constraint frame
        // 2nd term: transform from camera frame to worldspace
        // net: transform from camera frame to constraint frame, and spherical angle of camera frame wrt constraint frame
        const qPre : Quaternion = this.constraintFrame.rotation.clone().inverse().multiply(this.camera.quaternion);
        const sphPre : SphericalWithRoll = new SphericalWithRoll().setFromQuat(qPre);
        var sphClamp : SphericalWithRoll = rubberband == 'rubberband' ? this.LimitSpherical(sphPre) : sphPre.clone();
        if (rubberband == 'none') {
            // Only do a "hard" roll enforcement if we *already had* zero roll.
            // Otherwise, let the separate gentle rubberband code correct the roll for us.
            // (e.g., when user starts at an "askew" bookmark, starts interacting - we want to
            // gently level out).
            if (Math.abs(preRoll) < 0.1 * _Math.DEG2RAD)
                sphClamp.roll = 0;
        }
        else
            sphClamp.roll = 0;
        if (sphClamp.isEqual(sphPre, 0.001 * _Math.DEG2RAD))
            // Within limits. No rubber band required.
            return;
        const qClamp : Quaternion = sphClamp.toQuat();
        // Rubber band: rotate to the clamped position over rubberBandDuration.
        // Non-rubber: rotate directly to the clamped position.

        let rubberBandDuration : number = 0.5;
/*        if (!bRubberBand && Math.abs(sphPre.roll) > 1 * _Math.DEG2RAD) {
            console.log("Turning on rubber band roll");
            bRubberBand = true;
            rubberBandDuration = 2.0;
        }*/
        var q : Quaternion = rubberband != 'none' ? qPre.clone().slerp(qClamp, Math.min(1, dt / rubberBandDuration))
            : qClamp.clone();

        // Now convert it to a delta against the initial state.
        // 1st term: constraint frame wrt camera frame
        // 2nd term: desired frame wrt constraint frame
        // net: desired frame wrt camera frame (an "action")
        q.multiplyQuaternions(qPre.clone().inverse(), q);
        this.ApplyAction(new RBT(q, new Vector3(0, 0, 0)));
        //m_SimpleScene->RequestRedraw();
    }


    private m_Orig: Vector2 = new Vector2;

    // Equivalent to C++ ProcessEvent(TouchEvent) with type=ET_TouchDown
    ProcessTouchDownEvent(position: Vector2) {
        this.m_Orig.copy(position);
        this.m_Orig.y = this.viewportDims.y - this.m_Orig.y; // Flip to lower-left origin, y-up.
    }
    // Equivalent to C++ ProcessEvent(TouchEvent) with type=ET_TouchMove
    ProcessTouchMoveEvent(position: Vector2) {
        var p : Vector2 = position.clone();
        p.y = this.viewportDims.y - p.y; // Flip to lower-left origin, y-up.

        const Delta : Vector2 = p.clone().sub(this.m_Orig );
        if (Delta.length() > 0.005) {
            // WebViewer doesn't have this mode.
            //if ( m_Mode == AM_Eulerball && m_DragDir == DD_Unset ) {
            //}

            // WebViewer: always true

            //if ( m_Mode != AM_Eulerball ||
            //   m_DragDir != DD_Unset ) // If eulerball, must have set direction already
            //{

            // Rubber band effect part #1: stretching the band. If the initial position is beyond the angle
            // limits, then make this movement much more sluggish - a "stiffness" is applied to the ball rotation,
            // which grows exponentially higher the further beyond the angle limit we go.
            // (TODO: only apply stiffness if the motion is pushing further past the angle limit.)

            // 1st term: transform from worldspace to constraint frame
            // 2nd term: transform from camera frame to worldspace
            // net: transform from camera frame to bookmark frame, and spherical angle of camera frame wrt constraint frame
            const qPre : Quaternion = this.constraintFrame.rotation.clone().inverse().multiply(this.camera.quaternion);
            const sphPre : SphericalWithRoll = new SphericalWithRoll().setFromQuat(qPre);
            const sphClamp : SphericalWithRoll = this.LimitSpherical(sphPre);
            // (radians)
            const clampEffect : number = Math.abs(sphClamp.theta - sphPre.theta) + Math.abs(sphClamp.getElevation() - sphPre.getElevation());
            // Double stiffness every 2 degrees past the clamp point
            const stiffness : number = Math.pow(2.0, clampEffect * _Math.RAD2DEG / 2.0);
            //console.log('rotateStart = ' + rotateStart.x + ',' + rotateStart.y + ' end = ' + rotateEnd.x + ',' + rotateEnd.y + '\n');
            this.ApplyAction(new RBT(//m_Mode == AM_Eulerball ? MoveEulerball(p, m_Orig) :
                this.bEgocentric? this.MoveEgocentric(p, this.m_Orig)
                                : this.MoveArcball(p, this.m_Orig, stiffness),
                new Vector3(0, 0, 0)));

            // Player: kill any camera roll.
            //if ( GCore.IsPlayer() )
            {
                // Roll control only works sensibly if the viewer can't go through the poles
                if (this.limits.Elevation && this.limits.ElevationMinDeg > -85.0 && this.limits.ElevationMaxDeg <= 85.0)
                    // Kill any camera roll. (Don't apply the other constraints, elevation/azimuth - that happens after release.)
                    this.ApplyConstraints('none', 1/60., sphPre.roll);
            }
            this.m_Scene.cameraAnimator.SetOnBookmarkPath(false);
            this.m_Orig.copy(p);
            //m_SimpleScene->RequestRedraw();
        }
    }
    // Equivalent to C++ ProcessEvent(TouchEvent) with type=ET_TouchUp
    ProcessTouchUpEvent(position: Vector2) {
    }
};
